import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Heart, Users, Shield, Clock, CheckCircle, BookOpen, AlertTriangle } from 'lucide-react';
export function HeroSection() {
  return <section className="pt-8 pb-16 px-4 relative max-w-[800px] mx-auto text-center overflow-hidden" data-id="element-110">
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-[#1B3A6B] opacity-[0.03] blur-[100px] -z-10 rounded-full" data-id="element-111"></div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} data-id="element-112">
        {/* ⚠️ WARNING BOX */}
        <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2,
        duration: 0.5
      }} className="mb-8 border-l-4 border-[#B83A52] bg-[#FDF0F3] rounded-r-xl p-5 text-left relative overflow-hidden shadow-sm" data-id="element-113">
          <p className="text-[#B83A52] font-bold text-xs uppercase tracking-widest mb-2 flex items-center gap-1.5" data-id="element-114">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-115" /> Importante
          </p>
          <p className="text-[#1C1C2E] text-sm leading-relaxed italic" data-id="element-116">
            "Lo que estás a punto de leer va a hacerte sentir que{' '}
            <span className="font-bold" data-id="element-117">alguien finalmente entiende</span> lo
            que llevas semanas sintiendo pero no has podido decir en voz alta."
          </p>
        </motion.div>

        {/* Pre-headline */}
        <div className="mb-6" data-id="element-118">
          <span className="inline-block py-1 px-3 rounded-full bg-[#EEF2F7] border border-[#1B3A6B]/10 text-[#1B3A6B] text-xs font-bold uppercase tracking-widest mb-3" data-id="element-119">
            Para familias que enfrentan el cáncer de un ser querido
          </span>
          <p className="text-[#B83A52] font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2" data-id="element-120">
            <Clock className="w-4 h-4" data-id="element-121" /> PORQUE EL TIEMPO CON ELLOS NO REGRESA
          </p>
        </div>

        {/* Headlines */}
        <h1 className="text-3xl md:text-5xl leading-[1.2] font-serif font-bold text-[#1C1C2E] mb-6 tracking-tight" data-id="element-122">
          "¿Y si el mayor dolor de tu familiar no es el cáncer...{' '}
          <br className="hidden md:block" data-id="element-123" />
          <span className="text-[#1B3A6B]" data-id="element-124">
            sino sentirse solo mientras tú estás ahí al lado?"
          </span>
        </h1>

        <h2 className="text-lg text-[#4A4A6A] leading-relaxed mb-8 max-w-2xl mx-auto font-medium" data-id="element-125">
          Esto no es un libro de medicina. Es la guía que te enseña a ser
          exactamente lo que necesita —{' '}
          <span className="text-[#B83A52] font-bold" data-id="element-126">
            antes de que el tiempo decida por ti.
          </span>
        </h2>

        <p className="text-sm text-[#8B8BAA] italic mb-10 max-w-lg mx-auto" data-id="element-127">
          Sigue leyendo. Lo que vas a descubrir puede cambiar completamente cómo
          vives estos días — y cómo él o ella los recuerda.
        </p>

        {/* Product Mockup */}
        <div className="my-10 mx-auto relative group max-w-[280px]" data-id="element-128">
          {/* Animated glow halo */}
          <motion.div animate={{
          scale: [1, 1.15, 1],
          opacity: [0.4, 0.7, 0.4]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute -inset-6 bg-gradient-to-b from-[#1B3A6B]/30 via-[#B83A52]/20 to-[#1B3A6B]/30 rounded-3xl blur-3xl" data-id="element-129" />
          {/* Floating image */}
          <motion.div animate={{
          y: [0, -12, 0]
        }} transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="relative" data-id="element-130">
            <img src="https://cdn.magicpatterns.com/uploads/b6mXwauyzTQX5WrXdT3wdS/1apoyo.png" alt="El Apoyo Incondicional — Guía Digital" className="relative w-full h-auto block drop-shadow-2xl" data-id="element-131" />
          </motion.div>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-10 text-left" data-id="element-132">
          <div className="bg-white p-4 rounded-xl border border-[#EEF2F7] shadow-sm flex items-start gap-3" data-id="element-133">
            <div className="bg-[#EEF2F7] p-2 rounded-full text-[#1B3A6B] shrink-0" data-id="element-134">
              <BookOpen className="w-4 h-4" data-id="element-135" />
            </div>
            <span className="font-medium text-[#1C1C2E] text-sm" data-id="element-136">
              Saber qué decir en los momentos más difíciles
            </span>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EEF2F7] shadow-sm flex items-start gap-3" data-id="element-137">
            <div className="bg-[#EEF2F7] p-2 rounded-full text-[#1B3A6B] shrink-0" data-id="element-138">
              <Users className="w-4 h-4" data-id="element-139" />
            </div>
            <span className="font-medium text-[#1C1C2E] text-sm" data-id="element-140">
              Organizar a la familia sin conflictos ni culpa
            </span>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EEF2F7] shadow-sm flex items-start gap-3" data-id="element-141">
            <div className="bg-[#FDF0F3] p-2 rounded-full text-[#B83A52] shrink-0" data-id="element-142">
              <Shield className="w-4 h-4" data-id="element-143" />
            </div>
            <span className="font-medium text-[#1C1C2E] text-sm" data-id="element-144">
              Cuidarte a ti mismo para poder cuidar a quien amas
            </span>
          </div>
          <div className="bg-white p-4 rounded-xl border border-[#EEF2F7] shadow-sm flex items-start gap-3" data-id="element-145">
            <div className="bg-[#FDF0F3] p-2 rounded-full text-[#B83A52] shrink-0" data-id="element-146">
              <Heart className="w-4 h-4" data-id="element-147" />
            </div>
            <span className="font-medium text-[#1C1C2E] text-sm" data-id="element-148">
              Estar presente de verdad — no solo de cuerpo
            </span>
          </div>
        </div>

        {/* Proof Line */}
        <div className="flex flex-wrap items-center justify-center gap-3 text-xs font-medium text-[#4A4A6A] mb-8 uppercase tracking-wide" data-id="element-149">
          <div className="flex items-center gap-1" data-id="element-150">
            <CheckCircle className="w-3.5 h-3.5 text-[#1B3A6B]" data-id="element-151" />
            <span data-id="element-152">7 capítulos documentados</span>
          </div>
          <span className="text-[#B83A52] hidden sm:inline" data-id="element-153">•</span>
          <div className="flex items-center gap-1" data-id="element-154">
            <CheckCircle className="w-3.5 h-3.5 text-[#1B3A6B]" data-id="element-155" />
            <span data-id="element-156">Recursos imprimibles</span>
          </div>
          <span className="text-[#B83A52] hidden sm:inline" data-id="element-157">•</span>
          <div className="flex items-center gap-1" data-id="element-158">
            <CheckCircle className="w-3.5 h-3.5 text-[#1B3A6B]" data-id="element-159" />
            <span data-id="element-160">Basado en psicooncología</span>
          </div>
        </div>

        {/* CTA */}
        <motion.a href="#comprar" animate={{
        scale: [1, 1.03, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="relative block w-full overflow-hidden bg-[#B83A52] text-white font-bold py-5 px-6 rounded-xl text-lg uppercase tracking-wide shadow-[0_10px_30px_rgba(184,58,82,0.45)] mb-4 flex items-center justify-center gap-2 cursor-pointer" data-id="element-161">
          {/* Shimmer sweep */}
          <motion.span animate={{
          x: ['-100%', '200%']
        }} transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'linear',
          repeatDelay: 1
        }} className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent skew-x-[-20deg] pointer-events-none" data-id="element-162" />
          <span className="relative z-10 flex items-center gap-2" data-id="element-163">
            QUIERO SER EL APOYO QUE MI FAMILIAR NECESITA
            <motion.span animate={{
            x: [0, 5, 0]
          }} transition={{
            duration: 0.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-164">
              →
            </motion.span>
          </span>
        </motion.a>

        {/* Trust Micro-line */}
        <div className="flex items-center justify-center gap-2 text-xs text-[#4A4A6A] mb-12" data-id="element-165">
          <Shield className="w-3 h-3 text-[#1B3A6B]" data-id="element-166" />
          <span data-id="element-167">Garantía 30 días</span>
          <span className="text-[#B83A52] mx-1" data-id="element-168">|</span>
          <span data-id="element-169">Acceso inmediato</span>
          <span className="text-[#B83A52] mx-1" data-id="element-170">|</span>
          <span data-id="element-171">Sin riesgo</span>
        </div>

        <div className="flex justify-center" data-id="element-172">
          <ArrowDown className="w-6 h-6 text-[#1B3A6B] animate-bounce opacity-40" data-id="element-173" />
        </div>
      </motion.div>
    </section>;
}